import { createSlice, PayloadAction, createSelector } from "@reduxjs/toolkit";
import { BudgetRange } from "../../components/Filters/types";
import { getDefaultDuration } from "../../utils/constants/durationDefaults";

const pmsDefaultDuration = getDefaultDuration();

export interface PmsFilterState {
  pmsProperties: {
    destinationCountry: {
      id: number | null;
      value: string;
      displayValue: string;
    };
    city: {
      id: number | null;
      value: string;
      displayValue: string;
    };
    university: {
      id: number | null;
      value: string;
      displayValue: string;
    };
    budget: {
      value: BudgetRange[];
      displayValue: string[];
    };
    roomType: {
      value: string[];
      displayValue: string[];
    };
  };
  pmsLogs: {
    duration: {
      value: string;
      displayValue: string;
      startDate: string;
      endDate: string;
    };
  };
}

const initialState: PmsFilterState = {
  pmsProperties: {
    destinationCountry: {
      id: null,
      value: "",
      displayValue: "",
    },
    city: {
      id: null,
      value: "",
      displayValue: "",
    },
    university: {
      id: null,
      value: "",
      displayValue: "",
    },
    budget: {
      value: [],
      displayValue: [],
    },
    roomType: {
      value: [],
      displayValue: [],
    },
  },
  // PMS Logs page filters
  pmsLogs: {
    duration: {
      value: pmsDefaultDuration.value,
      displayValue: pmsDefaultDuration.displayValue,
      startDate: pmsDefaultDuration.startDate,
      endDate: pmsDefaultDuration.endDate,
    },
  },
};

export const pmsFilterSlice = createSlice({
  name: "pmsFilter",
  initialState,
  reducers: {

    setPmsDestinationCountry: (
      state,
      action: PayloadAction<{ id: number | null; value: string; displayValue?: string }>
    ) => {
      state.pmsProperties.destinationCountry.id = action.payload.id;
      state.pmsProperties.destinationCountry.value = action.payload.value;
      state.pmsProperties.destinationCountry.displayValue =
        action.payload.displayValue || action.payload.value;
    },
    clearPmsDestinationCountry: (state) => {
      state.pmsProperties.destinationCountry.id = null;
      state.pmsProperties.destinationCountry.value = "";
      state.pmsProperties.destinationCountry.displayValue = "";
    },

    setPmsCity: (
      state,
      action: PayloadAction<{ id: number | null; value: string; displayValue?: string }>
    ) => {
      state.pmsProperties.city.id = action.payload.id;
      state.pmsProperties.city.value = action.payload.value;
      state.pmsProperties.city.displayValue =
        action.payload.displayValue || action.payload.value;
    },
    clearPmsCity: (state) => {
      state.pmsProperties.city.id = null;
      state.pmsProperties.city.value = "";
      state.pmsProperties.city.displayValue = "";
    },

    setPmsUniversity: (
      state,
      action: PayloadAction<{ id: number | null; value: string; displayValue?: string }>
    ) => {
      state.pmsProperties.university.id = action.payload.id;
      state.pmsProperties.university.value = action.payload.value;
      state.pmsProperties.university.displayValue =
        action.payload.displayValue || action.payload.value;
    },
    clearPmsUniversity: (state) => {
      state.pmsProperties.university.id = null;
      state.pmsProperties.university.value = "";
      state.pmsProperties.university.displayValue = "";
    },

    setPmsBudget: (
      state,
      action: PayloadAction<{
        value: BudgetRange[];
        displayValue?: string[];
      }>
    ) => {
      state.pmsProperties.budget.value = action.payload.value;
      state.pmsProperties.budget.displayValue =
        action.payload.displayValue || [];
    },
    clearPmsBudget: (state) => {
      state.pmsProperties.budget.value = [];
      state.pmsProperties.budget.displayValue = [];
    },

    setPmsRoomType: (
      state,
      action: PayloadAction<{ value: string[]; displayValue?: string[] }>
    ) => {
      state.pmsProperties.roomType.value = action.payload.value;
      state.pmsProperties.roomType.displayValue =
        action.payload.displayValue || action.payload.value;
    },
    clearPmsRoomType: (state) => {
      state.pmsProperties.roomType.value = [];
      state.pmsProperties.roomType.displayValue = [];
    },

    clearAllPmsPropertiesFilters: (state) => {
      state.pmsProperties = initialState.pmsProperties;
    },


    setPmsLogsDuration: (
      state,
      action: PayloadAction<{
        startDate: string;
        endDate: string;
        displayValue?: string;
        value?: string;
      }>
    ) => {
      state.pmsLogs.duration.startDate = action.payload.startDate;
      state.pmsLogs.duration.endDate = action.payload.endDate;
      state.pmsLogs.duration.displayValue = action.payload.displayValue || "";
      state.pmsLogs.duration.value = action.payload.value || "";
    },
    clearPmsLogsDuration: (state) => {
      state.pmsLogs.duration.startDate = pmsDefaultDuration.startDate;
      state.pmsLogs.duration.endDate = pmsDefaultDuration.endDate;
      state.pmsLogs.duration.value = pmsDefaultDuration.value;
      state.pmsLogs.duration.displayValue = pmsDefaultDuration.displayValue;
    },

    clearAllPmsLogsFilters: (state) => {
      state.pmsLogs = initialState.pmsLogs;
    },

    // Reset all PMS filters
    resetAllPmsFilters: () => initialState,
  },
});

// Export actions
export const {
  // PMS Properties actions
  setPmsDestinationCountry,
  clearPmsDestinationCountry,
  setPmsCity,
  clearPmsCity,
  setPmsUniversity,
  clearPmsUniversity,
  setPmsBudget,
  clearPmsBudget,
  setPmsRoomType,
  clearPmsRoomType,
  clearAllPmsPropertiesFilters,

  // PMS Logs actions
  setPmsLogsDuration,
  clearPmsLogsDuration,
  clearAllPmsLogsFilters,

  // Reset all
  resetAllPmsFilters,
} = pmsFilterSlice.actions;

// Base selector
export const selectPmsFilterState = (state: { pmsFilter: PmsFilterState }) =>
  state.pmsFilter;

// PMS Properties page selectors
export const selectPmsPropertiesFilters = (state: {
  pmsFilter: PmsFilterState;
}) => state.pmsFilter.pmsProperties;

export const selectPmsDestinationCountry = (state: {
  pmsFilter: PmsFilterState;
}) => state.pmsFilter.pmsProperties.destinationCountry;

export const selectPmsCity = (state: { pmsFilter: PmsFilterState }) =>
  state.pmsFilter.pmsProperties.city;

export const selectPmsUniversity = (state: { pmsFilter: PmsFilterState }) =>
  state.pmsFilter.pmsProperties.university;

export const selectPmsBudget = (state: { pmsFilter: PmsFilterState }) =>
  state.pmsFilter.pmsProperties.budget;

export const selectPmsRoomType = (state: { pmsFilter: PmsFilterState }) =>
  state.pmsFilter.pmsProperties.roomType;

// PMS Logs page selectors
export const selectPmsLogsFilters = (state: { pmsFilter: PmsFilterState }) =>
  state.pmsFilter.pmsLogs;

export const selectPmsLogsDuration = (state: { pmsFilter: PmsFilterState }) =>
  state.pmsFilter.pmsLogs.duration;

// Computed selectors for PMS Properties page
export const selectPmsPropertiesActiveFilterCount = createSelector(
  [selectPmsPropertiesFilters],
  (filters) => {
    let count = 0;
    if (filters.destinationCountry.id !== null) count++;
    if (filters.city.id !== null) count++;
    if (filters.university.id !== null) count++;
    if (filters.budget.value.length > 0) count++;
    if (filters.roomType.value.length > 0) count++;
    return count;
  }
);

export const enableClearAllPmsPropertiesFilters = createSelector(
  [selectPmsPropertiesFilters],
  (filters) => {
    return (
      filters.destinationCountry.id !== null ||
      filters.city.id !== null ||
      filters.university.id !== null ||
      filters.budget.value.length > 0 ||
      filters.roomType.value.length > 0
    );
  }
);

// Computed selectors for PMS Logs page
export const selectPmsLogsActiveFilterCount = createSelector(
  [selectPmsLogsFilters],
  (filters) => {
    let count = 0;
    if (filters.duration.value && filters.duration.value !== pmsDefaultDuration.value)
      count++;
    return count;
  }
);

export const enableClearAllPmsLogsFilters = createSelector(
  [selectPmsLogsFilters],
  (filters) => {
    return filters.duration.value !== pmsDefaultDuration.value;
  }
);

// Utility function to build query string from PMS filters
export const buildPmsPropertiesQueryString = (
  filters: PmsFilterState["pmsProperties"]
): string => {
  const queryParams = new URLSearchParams();

  if (filters.destinationCountry.id !== null) {
    queryParams.append("country", String(filters.destinationCountry.id));
  }

  if (filters.city.id !== null) {
    queryParams.append("city", String(filters.city.id));
  }

  if (filters.university.id !== null) {
    queryParams.append("university", String(filters.university.id));
  }

  if (filters.roomType.value.length > 0) {
    queryParams.append("room_kind", filters.roomType.value.join(","));
  }

  if (filters.budget.value.length > 0) {
    const budgetRanges = filters.budget.value;
    const minPrices = budgetRanges.map((b) => b.min_budget).filter((v) => v !== undefined);
    const maxPrices = budgetRanges.map((b) => b.max_budget).filter((v) => v !== undefined);

    if (minPrices.length > 0) {
      queryParams.append("min_price", String(Math.min(...minPrices)));
    }
    if (maxPrices.length > 0) {
      queryParams.append("max_price", String(Math.max(...maxPrices)));
    }
  }

  return queryParams.toString();
};

export default pmsFilterSlice.reducer;

